import { marketingColors as C, marketingFonts as F, marketingShadow, visualTokens as V } from '../../theme_landing_page_2';
import React, { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Drawer, IconButton, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import useMarketingFonts from '../useMarketingFonts';
import { MillosMark, MillosWordmark } from './icons';

const ChevronDown: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>
    <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const complianceItems = ['Certificate vault & expiry AI', 'Books the engineer for you', 'New certificates filed on arrival', 'Gas Safety, EPC, EICR, fire & HMO'];
const accountingItems = ['Read-only Open Banking', 'Rent tracking per flat', 'AI invoice & expense capture', 'Scoped accountant access'];
const icpLinks = [
  { label: 'Letting agents', to: '/for/letting-agents' },
  { label: 'Landlords', to: '/for/portfolio-landlords' },
];

const hubGroups: { n: string; title: string; bg: string; links: { title: string; desc: string; to: string }[] }[] = [
  {
    n: '1',
    title: 'Learn',
    bg: C.millosBlue,
    links: [
      { title: 'Blog', desc: 'Compliance, tax & accounting', to: '/blog' },
      { title: 'Guides', desc: '7 long-form pillar guides', to: '/guides' },
      { title: 'Checklists', desc: 'Step-by-step compliance', to: '/checklists' },
      { title: 'News', desc: 'Product & industry updates', to: '/news' },
    ],
  },
  {
    n: '2',
    title: 'Tools',
    bg: C.ink,
    links: [
      { title: 'Calculators', desc: '10 free interactive tools', to: '/calculators' },
      { title: 'Templates', desc: 'Spreadsheets & documents', to: '/templates' },
      { title: 'Certificates Centre', desc: 'The compliance pillar', to: '/compliance-centre' },
      { title: 'Accounting Centre', desc: 'The accounting pillar', to: '/accounting-centre' },
    ],
  },
  {
    n: '3',
    title: 'Proof',
    bg: V.success,
    links: [
      { title: 'Case Studies', desc: 'Real landlord results', to: '/case-studies' },
      { title: 'How MillOS works', desc: 'See the flow it runs', to: '/how-it-works' },
      { title: 'Customers', desc: 'Who uses MillOS', to: '/customers' },
      { title: 'Who we serve', desc: 'Savings by segment', to: '/who-we-serve' },
      { title: 'Help / FAQ', desc: 'Answers & support', to: '/help' },
    ],
  },
];

const navLinkStyle: React.CSSProperties = { padding: '12px 16px', display: 'inline-block' };

const MarketingNav: React.FC = () => {
  useMarketingFonts();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [open, setOpen] = useState<'features' | 'learn' | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoHover, setLogoHover] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(() => setOpen(null), 250);
  };
  const close = () => {
    cancelScheduledClose();
    setOpen(null);
  };
  const openMenu = (menu: 'features' | 'learn') => {
    cancelScheduledClose();
    setOpen(menu);
  };
  const handleDropdownLinkClick = (e: React.MouseEvent) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      e.preventDefault();
      return;
    }
    close();
  };

  return (
    <Box
      className="lv-marketing"
      onMouseLeave={scheduleClose}
      sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, background: C.white, fontFamily: F.body }}
    >
      <Box sx={{ maxWidth: 1280, margin: '0 auto', position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1.5, md: 3 },
            background: C.white,
            padding: '14px 14px 14px 16px',
          }}
        >
          <RouterLink
            to="/"
            onClick={close}
            onMouseEnter={() => setLogoHover(true)}
            onMouseLeave={() => setLogoHover(false)}
            style={{ display: 'flex', alignItems: 'center', gap: 13 }}
          >
            <MillosMark size={44} rotation={logoHover ? 90 : 0} style={{ flex: 'none', transition: 'transform .6s cubic-bezier(.3,1.35,.5,1)' }} />
            <MillosWordmark fontSize={V.wordmarkSize} />
          </RouterLink>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: V.navSize, fontWeight: V.fontWeight500, color: V.body }}>
              <Box onMouseEnter={() => openMenu('features')} sx={{ position: 'static' }}>
                <Box
                  component="button"
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={open === 'features'}
                  onClick={() => (open === 'features' ? close() : openMenu('features'))}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '12px 16px', border: 0, cursor: 'pointer', font: 'inherit', borderRadius: V.borderRadius24, background: open === 'features' ? C.panel : 'transparent', color: 'inherit' }}
                >
                  Products
                  <ChevronDown open={open === 'features'} />
                </Box>
              </Box>
              <RouterLink to="/pricing" onMouseEnter={close} style={{ ...navLinkStyle, color: 'inherit' }}>
                Pricing
              </RouterLink>
              <Box onMouseEnter={() => openMenu('learn')} sx={{ position: 'static' }}>
                <RouterLink
                  to="/resources"
                  onClick={close}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 16px', borderRadius: V.borderRadius24, background: open === 'learn' ? C.panel : 'transparent', color: 'inherit' }}
                >
                  Learn
                  <ChevronDown open={open === 'learn'} />
                </RouterLink>
              </Box>
              <RouterLink to="/how-it-works" onMouseEnter={close} style={{ ...navLinkStyle, color: 'inherit' }}>
                How it works
              </RouterLink>
            </Box>
          )}

          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <RouterLink to="/login" style={{ fontSize: V.navSize, fontWeight: V.fontWeight500, color: C.navy, padding: '8px 6px' }}>
                Sign in
              </RouterLink>
              <Box
                component={RouterLink}
                to="/register"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: C.millosMint,
                  color: V.buttonText,
                  fontWeight: V.fontWeight600,
                  borderRadius: V.borderRadius32px,
                  padding: '12px 22px',
                  fontSize: V.navButtonSize,
                  whiteSpace: 'nowrap',
                  '&:hover': { background: C.millosBlue, color: C.white },
                }}
              >
                Start free
              </Box>
            </Box>
          ) : (
            <IconButton onClick={() => setMobileOpen(true)} aria-label="Open menu" sx={{ color: C.ink }}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        {!isMobile && open === 'features' && (
          <Box
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={scheduleClose}
            sx={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, background: C.white, borderRadius: V.borderRadius24px, boxShadow: marketingShadow.dropdown, padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 0.92fr', gap: '16px' }}
          >
            <Box
              component={RouterLink}
              to="/compliance-centre"
              onClick={handleDropdownLinkClick}
              sx={{ display: 'block', background: C.panel, borderRadius: V.borderRadius20px, padding: '26px', color: 'inherit', transition: 'background 0.15s', '&:hover': { background: C.tintBlue } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '14px' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: V.borderRadius12px, background: C.millosBlue, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </Box>
                <Box>
                  <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize22, color: C.ink }}>Certificates agent</Box>
                  <Box sx={{ fontSize: V.fontSize12, color: C.body }}>Books renewals, chases certificates</Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {complianceItems.map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: V.fontSize13_5, color: C.body }}>
                    <Box component="span" sx={{ width: 5, height: 5, borderRadius: V.borderRadius9px, background: C.millosBlue, flex: 'none' }} />
                    {i}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box
              component={RouterLink}
              to="/accounting-centre"
              onClick={handleDropdownLinkClick}
              sx={{ display: 'block', background: C.panel, borderRadius: V.borderRadius20px, padding: '26px', color: 'inherit', transition: 'background 0.15s', '&:hover': { background: C.tintBlue } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '14px' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: V.borderRadius12px, background: C.ink, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18" />
                    <path d="M5 21V8l7-4 7 4v13" />
                    <path d="M9 21v-5h6v5" />
                  </svg>
                </Box>
                <Box>
                  <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize22, color: C.ink }}>Accounting agent</Box>
                  <Box sx={{ fontSize: V.fontSize12, color: C.body }}>Reconciles rent, files your tax</Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {accountingItems.map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: V.fontSize13_5, color: C.body }}>
                    <Box component="span" sx={{ width: 5, height: 5, borderRadius: V.borderRadius9px, background: C.ink, flex: 'none' }} />
                    {i}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ borderRadius: V.borderRadius20px, padding: '24px 22px', border: `1px solid ${C.glassBorder}` }}>
              <Box sx={{ fontSize: V.fontSize11, fontWeight: V.fontWeight600, letterSpacing: V.letterSpacing0_08em, textTransform: 'uppercase', color: C.millosBlue, mb: '14px' }}>Who it&apos;s built for</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {icpLinks.map((a) => (
                  <Box
                    key={a.to}
                    component={RouterLink}
                    to={a.to}
                    onClick={handleDropdownLinkClick}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '9px 11px', borderRadius: V.borderRadius11px, fontSize: V.fontSize13_5, color: C.ink, '&:hover': { background: C.tintBlue, color: C.millosBlue } }}
                  >
                    {a.label}
                    <Box component="span" sx={{ color: C.millosBlue, fontSize: V.fontSize12 }}>→</Box>
                  </Box>
                ))}
              </Box>
              <Box component={RouterLink} to="/who-we-serve" onClick={handleDropdownLinkClick} sx={{ display: 'block', mt: '12px', padding: '9px 11px', borderRadius: V.borderRadius11px, fontSize: V.fontSize12_5, color: C.body, '&:hover': { background: C.tintBlue, color: C.millosBlue } }}>
                Compare all segments →
              </Box>
            </Box>
          </Box>
        )}

        {!isMobile && open === 'learn' && (
          <Box
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={scheduleClose}
            sx={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, background: C.white, borderRadius: V.borderRadius24px, boxShadow: marketingShadow.dropdown, padding: '22px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '22px' }}
          >
            {hubGroups.map((g) => (
              <Box key={g.title}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '12px', pb: '10px', borderBottom: `1px solid ${C.tintDivider}` }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: V.borderRadius8px, background: g.bg, display: 'grid', placeItems: 'center', flex: 'none', fontSize: V.fontSize12, color: C.white, fontWeight: V.fontWeight600 }}>{g.n}</Box>
                  <Box sx={{ fontSize: V.fontSize11, fontWeight: V.fontWeight600, letterSpacing: V.letterSpacing0_08em, textTransform: 'uppercase', color: V.muted }}>{g.title}</Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {g.links.map((r) => (
                    <Box
                      component={RouterLink}
                      key={r.to}
                      to={r.to}
                      onClick={handleDropdownLinkClick}
                      sx={{ display: 'block', padding: '9px 11px', borderRadius: V.borderRadius11px, color: 'inherit', '&:hover': { background: C.panel } }}
                    >
                      <Box sx={{ fontSize: V.fontSize13_5, fontWeight: V.fontWeight500, color: C.ink }}>{r.title}</Box>
                      <Box sx={{ fontSize: V.fontSize11_5, color: V.muted, marginTop: '1px' }}>{r.desc}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 280, p: 3, display: 'flex', flexDirection: 'column', gap: 1, fontFamily: F.body }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          </Box>
          {[
            { label: 'Certificates', to: '/compliance-centre' },
            { label: 'Accounting', to: '/accounting-centre' },
            { label: 'Pricing', to: '/pricing' },
            { label: 'Compare', to: '/compare' },
            { label: 'Who we serve', to: '/who-we-serve' },
            { label: 'Resources', to: '/resources' },
            { label: 'How it works', to: '/how-it-works' },
            { label: 'Customers', to: '/customers' },
          ].map((item) => (
            <RouterLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)} style={{ padding: '12px 8px', fontSize: V.fontSize16, color: C.ink, borderBottom: `1px solid ${C.hairline}` }}>
              {item.label}
            </RouterLink>
          ))}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            <Box
              component="button"
              onClick={() => {
                setMobileOpen(false);
                navigate('/login');
              }}
              sx={{ border: `1px solid ${C.hairline}`, borderRadius: V.borderRadius32px, padding: '11px 20px', fontSize: V.fontSize15, background: 'none', cursor: 'pointer', fontFamily: F.body }}
            >
              Sign in
            </Box>
            <Box
              component="button"
              onClick={() => {
                setMobileOpen(false);
                navigate('/register');
              }}
              sx={{ border: 'none', borderRadius: V.borderRadius32px, padding: '11px 20px', fontSize: V.fontSize15, background: C.millosMint, color: V.buttonText, fontWeight: V.fontWeight600, cursor: 'pointer', fontFamily: F.body }}
            >
              Start free
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default MarketingNav;
